/**
 * Resonow theme system — Light / Dark / System
 *
 * Applies the `dark` class to <html> (Tailwind class strategy) and persists
 * the user's preference in localStorage. When mode is "system", we follow
 * `prefers-color-scheme` and react to OS changes live.
 */
export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'resonow_theme_v1';
const LEGACY_KEY = 'resonow_dark_preview_v1';

const root = () => document.documentElement;

const prefersDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const apply = (mode: ThemeMode) => {
  const dark = mode === 'dark' || (mode === 'system' && prefersDark());
  root().classList.toggle('dark', dark);
  root().style.colorScheme = dark ? 'dark' : 'light';
};

export const getTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'system';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    if (v === 'light' || v === 'dark' || v === 'system') return v;
    // Migrate legacy preview-only flag
    const legacy = window.localStorage.getItem(LEGACY_KEY);
    if (legacy === '1') return 'dark';
  } catch {
    /* ignore */
  }
  return 'system';
};

export const setTheme = (mode: ThemeMode) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
  apply(mode);
  window.dispatchEvent(new CustomEvent('resonow:theme', { detail: mode }));
};

/** Called once at startup. Safe to import for side effects. */
export const initTheme = () => {
  const mode = getTheme();
  apply(mode);

  // Live-follow OS changes while in system mode
  if (window.matchMedia) {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (getTheme() === 'system') apply('system');
    };
    try {
      mql.addEventListener('change', onChange);
    } catch {
      // Safari < 14
      mql.addListener(onChange);
    }
  }
};
